import React, { useRef, useEffect, useState } from 'react';

type Cell = {
  x: number;
  y: number;
  colors: string[];
  opacity: number;
};

const CELL_SIZE = 56;
const FADE_SPEED = 0.009;
const COLORS_PER_CELL = 4;

const COLOR_POOL = ['#7A69FA'];
const BORDER_COLOR = '#F2DDFF';

const HoverGridCanvas: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const cellsRef = useRef<Map<string, Cell>>(new Map());

  const [size, setSize] = useState({ width: 0, height: 0 });

  const randomColor = () => COLOR_POOL[Math.floor(Math.random() * COLOR_POOL.length)];
  const getKey = (x: number, y: number) => `${x},${y}`;

  useEffect(() => {
    const currentContainer = containerRef.current;
    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const { width, height } = entry.contentRect;
        setSize({ width, height });
      }
    });

    if (currentContainer) {
      observer.observe(currentContainer);
    }

    return () => {
      if (currentContainer) {
        observer.unobserve(currentContainer);
      }
    };
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || size.width === 0 || size.height === 0) return;

    const ctx = canvas.getContext('2d')!;
    canvas.width = size.width;
    canvas.height = size.height;

    const cols = Math.ceil(size.width / CELL_SIZE);
    const rows = Math.ceil(size.height / CELL_SIZE);

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      for (let i = 0; i < cols; i++) {
        for (let j = 0; j < rows; j++) {
          const x = i * CELL_SIZE;
          const y = j * CELL_SIZE;
          const key = getKey(i, j);
          const cell = cellsRef.current.get(key);

          if (cell && cell.opacity > 0) {
            cell.colors.forEach((color) => {
              ctx.fillStyle = color;
              ctx.globalAlpha = cell.opacity;
              ctx.fillRect(x, y, CELL_SIZE, CELL_SIZE);
            });
            ctx.globalAlpha = 1.0;

            cell.opacity -= FADE_SPEED;
            if (cell.opacity <= 0) {
              cellsRef.current.delete(key);
            } else {
              cellsRef.current.set(key, cell);
            }
          }

          // Border ô
          ctx.strokeStyle = BORDER_COLOR;
          ctx.strokeRect(x, y, CELL_SIZE, CELL_SIZE);
        }
      }

      requestAnimationFrame(draw);
    };

    draw();

    const handleMouseMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      const col = Math.floor(mouseX / CELL_SIZE);
      const row = Math.floor(mouseY / CELL_SIZE);
      const key = getKey(col, row);

      if (!cellsRef.current.has(key)) {
        cellsRef.current.set(key, {
          x: col,
          y: row,
          colors: Array.from({ length: COLORS_PER_CELL }, randomColor),
          opacity: 1,
        });
      }
    };

    canvas.addEventListener('mousemove', handleMouseMove);
    return () => {
      canvas.removeEventListener('mousemove', handleMouseMove);
    };
  }, [size]);

  return (
    <div ref={containerRef} className="hover-grid-canvas">
      <canvas
        ref={canvasRef}
        style={{
          display: 'block',
          width: '100%',
          height: '100%',
          cursor: 'pointer',
        }}
      />
    </div>
  );
};

export default HoverGridCanvas;
